import { Inject, Injectable } from '@angular/core';
import { LoggerProvider, SimpleLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { LogAttributes, SeverityNumber } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { Logger } from '@opentelemetry/api-logs';
import { resourceFromAttributes } from '@opentelemetry/resources';

export enum LogLevel {
    All = 0,
    Debug = 1,
    Info = 2,
    Warn = 3,
    Error = 4,
    Fatal = 5,
    Off = 6
}

export class LogEntry {
    // Public Properties
    entryDate: Date = new Date();
    message: string = "";
    level: LogLevel = LogLevel.Debug;
    extraInfo: any[] = [];
    logWithDate: boolean = true;
    
    buildLogString(): string {
        let ret: string = "";
        
        if (this.logWithDate) {
            ret = this.entryDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) + " " + this.entryDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + " >> ";
        }
        
        ret += this.message;
        if (this.extraInfo.length) {
            ret += " - Extra Info: " + this.formatParams(this.extraInfo);
        }
        
        return ret;
    }
    
    private formatParams(params: any[]): string {
        let ret: string = params.join(",");
        
        // Is there at least one object in the array?
        if (params.some(p => typeof p == "object")) {
            ret = "";
            
            // Build comma-delimited string
            for (let item of params) {
                ret += JSON.stringify(item) + ",";
            }
        }
        
        return ret;
    }
}


@Injectable({
    providedIn: 'root'
})
export class LogService {
    private level: number = LogLevel.Debug;
    // Log Date
    private logWithDate: boolean = true;
    private logger: Logger;

    constructor() {
        const loggerProvider = new LoggerProvider({
            resource: resourceFromAttributes({
                "service.name": "stela.frontend",
                "service.version": "1.0.0",
                "service.instance.id": window.location.hostname
            }),
            processors: [ new SimpleLogRecordProcessor(new OTLPLogExporter({
                url: 'https://monitor.stela.pro/v1/logs',
                headers: {
                    "x-api-key": "dc1de862-3fce-4251-ab9e-0ac3caf0ca25"
                }
            }))]
        });
        this.logger = loggerProvider.getLogger('default');
    }

    debug(msg: string, attributes?: LogAttributes, ...optionalParams: any[]) {
        this.writeToLog(msg, LogLevel.Debug, optionalParams);
    }
    
    info(msg: string, attributes?: LogAttributes, ...optionalParams: any[]) {
        this.writeToLog(msg, LogLevel.Info, optionalParams);
    }
    
    warn(msg: string, attributes?: LogAttributes, ...optionalParams: any[]) {
        let user = localStorage.getItem("userName");
        attributes = { ...attributes, user: user };
        this.logger.emit({
            body: msg,
            severityText: 'WARN',
            severityNumber: SeverityNumber.WARN,
            attributes: attributes
        });
        this.writeToLog(msg, LogLevel.Warn, optionalParams);
    }
    
    error(msg: string, attributes?: LogAttributes, ...optionalParams: any[]) {
        let user = localStorage.getItem("userName");
        attributes = { ...attributes, user: user };
        this.logger.emit({
            body: msg,
            severityText: 'ERROR',
            severityNumber: SeverityNumber.ERROR,
            attributes: attributes
        });
        this.writeToLog(msg, LogLevel.Error, optionalParams);
    }
    
    fatal(msg: string, attributes?: LogAttributes, ...optionalParams: any[]) {
        this.writeToLog(msg, LogLevel.Fatal, optionalParams);
    }
    
    log(msg: string, attributes?: LogAttributes, ...optionalParams: any[]) {
        this.writeToLog(msg, LogLevel.All, optionalParams);
    }

    private writeToLog(msg: string, level: LogLevel, params: any[]) {
        if (this.shouldLog(level)) {
            let entry: LogEntry = new LogEntry();
            entry.message = msg;
            entry.level = level;
            entry.extraInfo = params;
            entry.logWithDate = this.logWithDate;
            // Depending on the level, log the entry using the appropriate console method
            switch (level) {
                case LogLevel.Debug:
                    console.debug(entry.buildLogString());
                    break;
                case LogLevel.Info:
                    console.info(entry.buildLogString());
                    break;
                case LogLevel.Warn:
                    console.warn(entry.buildLogString());
                    break;
                case LogLevel.Error:
                    console.error(entry.buildLogString());
                    break;
                case LogLevel.Fatal:
                    console.error(entry.buildLogString());
                    break;
                case LogLevel.All:
                    console.log(entry.buildLogString());
                    break;
            }
        }
    }
    
    private shouldLog(level: LogLevel): boolean {
        let ret: boolean = false;
        if ((level >= this.level && level !== LogLevel.Off) || this.level === LogLevel.All) {
            ret = true;
        }
        return ret;
    }

    private formatParams(params: any[]): string {
        let ret: string = params.join(",");
    
        // Is there at least one object in the array?
        if (params.some(p => typeof p == "object")) {
            ret = "";
            
            // Build comma-delimited string
            for (let item of params) {
                ret += JSON.stringify(item) + ",";
            }
        }
        return ret;
    }
}

